import ReviewsAPI from '@api/jooicez-backend/reviews';
import { IGetReviewsSourcesResponse } from '@models/reviews/response';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@utils/queryKeys';
import { ReactNode, createContext, useContext, useMemo, useState, useEffect } from 'react';

import { useApp } from './ApplicationContext';
import { usePolicy } from './PolicyContext';

export interface IReputationContext {
  // Reviews sources data
  reviewsSources: IGetReviewsSourcesResponse | undefined;
  isReviewsSourcesLoading: boolean;
  refetchReviewsSources: VoidFunction;
  hasAvailableSources: boolean;

  // Internal method to enable queries
  enableContext: VoidFunction;
}

const ReputationContext = createContext<IReputationContext>({
  // Reviews sources data
  reviewsSources: undefined,
  isReviewsSourcesLoading: false,
  refetchReviewsSources: () => {},
  hasAvailableSources: false,

  // Internal method to enable queries
  enableContext: () => {},
});

export const ReputationContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [enable, setEnable] = useState(false);
  const enableContext = () => setEnable(true);

  const { locationId } = useApp();
  const { hasReputation } = usePolicy();

  // Reviews sources query
  const {
    data: reviewsSourcesData,
    isFetching: isReviewsSourcesLoading,
    refetch: refetchReviewsSources,
  } = useQuery({
    queryKey: [queryKeys.reputation.reviewsSources, locationId],
    queryFn: () => ReviewsAPI.getReviewsSources(locationId ?? 0),
    enabled: !!locationId && hasReputation && enable,
    refetchOnWindowFocus: false,
  });

  // Extract data from query responses
  const reviewsSources = useMemo(() => reviewsSourcesData?.data, [reviewsSourcesData]);

  // Check if there are available sources with URLs
  const hasAvailableSources = useMemo(
    () => reviewsSources?.some((source) => source.url !== '') || false,
    [reviewsSources],
  );

  return (
    <ReputationContext.Provider
      value={{
        // Reviews sources data
        reviewsSources,
        isReviewsSourcesLoading,
        refetchReviewsSources,
        hasAvailableSources,

        // Internal method to enable queries
        enableContext,
      }}
    >
      {children}
    </ReputationContext.Provider>
  );
};

export const useReputation = () => {
  const context = useContext(ReputationContext);
  if (context === undefined) {
    throw new Error('useReputation must be used within a ReputationContextProvider');
  }

  // Enable queries on first use
  useEffect(() => {
    context.enableContext();
  }, [context]);

  return context;
};
